import { forwardRef, cloneElement } from "react";
import { useField } from "formik";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  PinInputDescendantsProvider,
  PinInputProvider,
  usePinInput,
  usePinInputField,
} from "@hooks/useOTP";
import { getValidChildren } from "@helpers/common";
import ErrorMessageDefault from "../../base/components/ErrorMessageDefault";

import styles from "./index.module.scss";

export const FormikPinInput = (props) => {
  const { children, className, name, customError, ...rest } = props;
  const { t } = useTranslation("errors");
  const [{ onBlur }, { value, error, touched }, { setValue, setTouched }] =
    useField(name);

  const handleChange = (value) => setValue(value);

  const handleBlur = (value) => {
    onBlur(value);
    setTouched(true, true);
  };

  const { descendants, ...context } = usePinInput({
    ...rest,
    value,
    error,
    onBlur: handleBlur,
    onChange: handleChange,
  });

  const clones = getValidChildren(children).map((child) => cloneElement(child));

  return (
    <section className={classNames("form-control-wrapper d-flex", className)}>
      <PinInputDescendantsProvider value={descendants}>
        <PinInputProvider value={context}>{clones}</PinInputProvider>
      </PinInputDescendantsProvider>
      {touched && (error || customError) && (
        <ErrorMessageDefault
          className={classNames("position-absolute", styles.errorMessage)}
        >
          {customError ? customError : t(error)}
        </ErrorMessageDefault>
      )}
    </section>
  );
};

export const PinInputField = forwardRef((props, ref) => {
  const inputProps = usePinInputField(props, ref);

  return (
    <input
      className={classNames(
        "form-control",
        styles.pinInputField,
        props.customError && styles.error,
      )}
      {...inputProps}
    />
  );
});

PinInputField.displayName = "PinInputField";
